import React from 'react';
import { Box, Button, makeStyles, Typography } from '@material-ui/core';
import classnames from 'classnames';
import Link from 'next/link';
import Image from 'next/image';

import { FeaturedProductPreviewWithColorCollection, ProductPreview } from 'domain/types';
import { useLinkSwipe } from 'utils/hooks/useLinkSwipe';
import CustomA from 'components/ui/links/CustomA';
import PriceText from 'components/ui/textFormaters/PriceText';
import { addToCart } from 'utils/apollo/vars/cartVar';
import { openCart } from 'utils/apollo/vars/showCartVar';
import events from 'utils/helpers/analytics/events';
import { calculatePrice } from 'utils/helpers/products/calculatePrice';

interface Props {
  product: FeaturedProductPreviewWithColorCollection | ProductPreview;
  containerClassName?: string;
  size?: 'small' | 'large';
}

const FeaturedProductCard: React.FC<Props> = ({ product, size = 'large' }): JSX.Element => {
  const classes = useStyles();

  const { handleMouseDown, handleClick } = useLinkSwipe();

  const {
    sys: { id },
    slug,
    mainImage: { url },
    name,
    price,
    discount,
    available,
    productItemCode,
    category: { slug: categorySlug, name: categoryName }
  } = product;

  const color = 'color' in product ? product.color : undefined;
  const cartItemIds = 'cartItemIds' in product ? product.cartItemIds : undefined;

  const handleBuy = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (cartItemIds?.length) {
      cartItemIds.forEach((cartItemId) => addToCart(cartItemId));
    } else {
      addToCart(id);
    }

    openCart();

    events.addToCart(
      categoryName,
      name,
      color,
      1,
      calculatePrice(price, discount),
      productItemCode
    );
  };

  return (
    <Link href={`/shop/${categorySlug}/${slug}`} passHref>
      <CustomA>
        <Box
          className={classes.cardContainer}
          onMouseDown={handleMouseDown}
          onClick={handleClick}
          component="article"
        >
          <Box className={classes.imageContainer}>
            <Image
              src={url}
              quality={75}
              width={284}
              height={284}
              layout="responsive"
              sizes="(max-width: 900px) 50vw, 25vw"
              className={classnames(classes.image, !available && classes.notAvailable)}
            />
          </Box>

          <Box className={classes.content}>
            <Typography
              variant="h3"
              component="h3"
              className={classnames(classes.title, size === 'small' && classes.titleSmall)}
            >
              {name}
            </Typography>

            <Typography
              variant="h5"
              component="p"
              color="textSecondary"
              className={classnames(classes.price, size === 'small' && classes.priceSmall)}
            >
              <PriceText price={price} discount={discount} />
            </Typography>

            <Button
              variant="contained"
              color="primary"
              onClick={handleBuy}
              disabled={available <= 0}
              className={classnames(
                classes.buyButton,
                available <= 0 && classes.buyButtonNotAvailable
              )}
            >
              {available > 0 ? 'У кошик' : 'Не в наявності'}
            </Button>
          </Box>
        </Box>
      </CustomA>
    </Link>
  );
};

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'box-shadow 500ms',
    [theme.breakpoints.up('md')]: {
      '&:hover': {
        boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.16)',
        '& $image': {
          transform: 'scale(1.07)'
        },
        '& $buyButton': {
          opacity: 1,
          pointerEvents: 'auto'
        }
      }
    }
  },
  imageContainer: {
    overflow: 'hidden',
    lineHeight: 0
  },
  image: {
    transition: 'transform 500ms ease-in'
  },
  notAvailable: {
    opacity: 0.59
  },
  content: {
    width: '100%',
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    textAlign: 'center',
    flexGrow: 1,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2)
    }
  },
  title: {
    marginBottom: theme.spacing(2)
  },
  price: {
    marginTop: 'auto'
  },
  titleSmall: {
    fontSize: '0.875rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1rem'
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1.25rem'
    }
  },
  priceSmall: {
    fontSize: '0.875rem',
    [theme.breakpoints.up('md')]: {
      fontSize: '1.25rem'
    }
  },
  buyButton: {
    marginTop: theme.spacing(1.5),
    fontSize: '0.8125rem',
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(2),
      fontSize: '1rem',
      opacity: 0,
      pointerEvents: 'none',
      transition: 'opacity 300ms'
    }
  },
  buyButtonNotAvailable: {
    fontSize: '0.6875rem',
    [theme.breakpoints.up('md')]: {
      fontSize: '0.875rem'
    }
  }
}));

export default FeaturedProductCard;
